
import { Component, Prop, Provide, Vue } from "vue-property-decorator";
import { CreateElement } from "vue";
import { DataModules } from "@/store/modules/data";
import { FraudStatus, FraudObjectType, FraudPolicies } from "@/data/enums";
import { FraudStatusMap } from "@/data/fraud";
import { IFraudObject, IFraudEvent } from "@/models/fraud";
import {
  IFraudMindProvider,
  IFraudMindProviderMethods
} from "@/models/providers/fraud";
import { IClient } from "@/models/clients";
import _ from "@/boot/lodash";
import { IProvider } from "@/models/providers";

@Component
export default class FraudMindProvider extends Vue implements IProvider {
  // Props
  // It can be basket(invoice), client, contract. All extending IFraudObject
  @Prop({
    required: true,
    validator: val => {
      // Pass object as a function to maintain reactivity
      return typeof val === "function";
    }
  })
  public object!: () => IFraudObject;

  @Prop({ default: "" })
  public readonly identifier!: string;

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({
    required: true,
    validator: value =>
      [
        FraudObjectType.CLIENT,
        FraudObjectType.ORDER,
        FraudObjectType.CONTRACT,
        FraudObjectType.INVOICE,
        FraudObjectType.BRAND,
        ""
      ].includes(value)
  })
  public type!: FraudObjectType;

  @Prop({ default: null })
  public readonly dataScope!: string;

  // Data
  public scope = this.dataScope || `$${this.type}_${this.fraudObject.id}`;
  public fraudEvent: IFraudEvent = {} as IFraudEvent;
  public clientPolicy: FraudPolicies | null = null;
  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public hasRequestError = false;
  public fraudStatusMap = FraudStatusMap();

  @Provide("fraudMindProviderMethods")
  public get providerMethods(): IFraudMindProviderMethods {
    return {
      openAddFraudRuleModal: this.openAddFraudRuleModal,
      openAnalysisModal: this.openAnalysisModal,
      changeStatus: this.changeStatus,
      getClientPolicy: this.getClientPolicy,
      getInvoiceFraudCheckData: this.getInvoiceFraudCheckData,
      openInvoiceFraudCheckData: this.openInvoiceFraudCheckData,
      setClientPolicy: this.setClientPolicy,
      reloadData: this.reloadData
    };
  }

  @Provide("fraudMindProvider")
  public providerData(): IFraudMindProvider {
    return {
      fraudObject: this.fraudObject,
      fraudStatusId: this.fraudStatusId,
      clientPolicy: this.clientPolicy,
      canGetInvoiceFraudCheckData: this.canGetInvoiceFraudCheckData,
      i18n: this.i18n,
      identifier: this.identifier,
      isBlocked: this.isBlocked,
      isProcessing: this.isProcessing,
      status: this.status,
      type: this.type,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError
    };
  }

  // Lifecycle methods
  public created() {
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.FRAUD_DETECTION_EVENTS,
      scope: this.scope,
      vm: this
    });
  }

  // Getters
  public get fraudObject() {
    return this.object();
  }

  public get clientId(): IClient["id"] {
    return _.get(
      this.fraudObject,
      this.type === FraudObjectType.CLIENT ? "id" : "client_id"
    );
  }

  public get i18n() {
    return {
      seeChanges: this.$t("_action.see_changes") as string,
      objectName: (this.type === FraudObjectType.INVOICE
        ? this.$t("_.invoice")
        : this.type === FraudObjectType.ORDER
        ? this.$t("_.order")
        : this.type === FraudObjectType.CONTRACT
        ? this.$t("_.contract")
        : this.$t("_.client")) as string
    };
  }

  public get fraudStatusId(): FraudStatus {
    return this.fraudObject?.fraud_status;
  }

  public get status() {
    return this.fraudStatusMap[this.fraudStatusId];
  }

  public get isBlocked() {
    return this.fraudStatusId === FraudStatus.FRAUD;
  }

  public get filterForType() {
    switch (this.type) {
      case FraudObjectType.CLIENT:
        return { "filter[client_id]": this.fraudObject.id };
      case FraudObjectType.CONTRACT:
        return { "filter[contract_id]": this.fraudObject.id };
      case FraudObjectType.ORDER:
      case FraudObjectType.INVOICE:
        return { "filter[invoice_id]": this.fraudObject.id };
      default:
        return {};
    }
  }

  public get canGetInvoiceFraudCheckData() {
    return this.$userCan("get_invoice_fraud_check_data");
  }

  // Methods
  public openAddFraudRuleModal() {
    return this.$store.dispatch(
      `data/${DataModules.FRAUD_DETECTION}/openAddEditModal`,
      {
        props: {
          rule: {
            type: "ip",
            value: _.get(this.fraudEvent, "invoice.ip", this.fraudObject.ip)
          }
        }
      }
    );
  }

  public changeStatus(fraud_status: FraudStatus) {
    this.isProcessing = true;
    return this.$store
      .dispatch(
        `data/${DataModules.FRAUD_DETECTION_EVENTS}/resolveFraudulentEvent`,
        {
          type: this.type,
          data: {
            id: this.fraudObject.id,
            fraud_status
          }
        }
      )
      .then((event: IFraudEvent) => {
        this.reloadData();
        this.$emit("refresh");
        this.$emit("success");
        this.$bus.$emit(`new-fraud-event`, event);
        return;
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }

  public loadData({ ignoreStored } = { ignoreStored: false }) {
    return this.$store
      .dispatch(`data/${DataModules.FRAUD_DETECTION_EVENTS}/list`, {
        vm: this,
        ignoreStored,
        type: this.type,
        scope: this.scope,
        params: {
          with: "invoice,resolver,origin",
          brand_id: this.fraudObject.brand_id,
          order: "-created_at",
          limit: 1,
          include_fields:
            "status,reason,id,created_at,contract_id,brand_id,invoice.id,invoice.ip",
          ...this.filterForType
        }
      })
      .then(events => {
        this.hasRequestError = false;
        this.fraudEvent = _.head(events) as IFraudEvent;
      })
      .catch(error => {
        this.hasRequestError = true;
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.isReloading = false;
      });
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData({ ignoreStored: true });
  }

  public async getClientPolicy() {
    const { fraud_policy } = await this.$store.dispatch(
      `data/${DataModules.CLIENTS}/get`,
      {
        vm: this,
        storeData: false,
        id: this.clientId,
        params: {
          include_fields: "fraud_policy"
        }
      }
    );
    this.clientPolicy = fraud_policy;
    return fraud_policy;
  }

  public async setClientPolicy(fraud_policy: FraudPolicies) {
    try {
      await this.$store.dispatch(
        `data/${DataModules.FRAUD_DETECTION_POLICIES}/update`,
        {
          vm: this,
          data: {
            id: this.clientId,
            fraud_policy
          }
        }
      );
      this.$buefy.toast.open({
        message: this.$t("_.object_successfully_updated", {
          object: this.$t("_.client_policy")
        }) as string
      });
      this.clientPolicy = fraud_policy;
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }
    return;
  }

  public async openAnalysisModal() {
    await this.$store.dispatch(
      `data/${DataModules.FRAUD_DETECTION}/openAnalysisModal`,
      {
        parent: this,
        events: {
          refresh: () => {
            this.$emit("refresh");
          },
          success: () => {
            this.$emit("success");
            this.reloadData();
          }
        }
      }
    );
  }

  public openInvoiceFraudCheckData({
    invoiceId
  }: {
    invoiceId: IFraudObject["id"];
  }) {
    return this.$store.dispatch("ui/open/slideModal", {
      config: {
        parent: this,
        component: () =>
          import("@/components/app/global/fraud/fraudCheckDataModal.vue"),
        props: {
          invoiceId
        }
      }
    });
  }

  public async getInvoiceFraudCheckData({
    invoiceId,
    ignoreStored
  }: {
    invoiceId: IFraudObject["id"];
    ignoreStored: boolean;
  }) {
    return this.$store.dispatch(
      `data/${DataModules.FRAUD_DETECTION}/getInvoiceFraudCheckData`,
      {
        ignoreStored,
        invoiceId,
        storeData: false
      }
    );
  }

  // Render
  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    // Renderless, no HTML is rendered for this component
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $fraudmindData: this.providerData(),
        $fraudmindMethods: this.providerMethods
      })
    );
  }
}
